import { useEffect, useMemo, useState } from "react";
import { api, fmtEur, formatErr, canWrite, CATEGORIE_LABELS, currentCompetenzaMese } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useCompany } from "@/contexts/CompanyContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MonthPicker } from "@/components/MonthPicker";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Split } from "lucide-react";
import { toast } from "sonner";

export default function Ripartizione() {
  const { company } = useCompany();
  const { user } = useAuth();
  const writable = canWrite(user, "ripartizione");
  const [azienda, setAzienda] = useState(company === "all" ? "golmar" : company);
  const [padroncini, setPadroncini] = useState([]);
  const [quote, setQuote] = useState({});
  const [head, setHead] = useState({
    mese_competenza: currentCompetenzaMese(), categoria: "telepass",
    descrizione: "", importo_totale: 0, data: new Date().toISOString().slice(0, 10),
  });

  useEffect(() => {
    api.get("/padroncini", { params: { company: azienda } }).then((r) => {
      setPadroncini(r.data.filter((p) => p.is_active)); setQuote({});
    });
  }, [azienda]);

  const somma = useMemo(() => Object.values(quote).reduce((a, v) => a + (parseFloat(v) || 0), 0), [quote]);
  const diff = Math.round((head.importo_totale - somma) * 100) / 100;

  const dividiEqui = () => {
    if (!padroncini.length) return;
    const q = Math.floor((head.importo_totale / padroncini.length) * 100) / 100;
    const out = Object.fromEntries(padroncini.map((p) => [p.id, q]));
    const resto = Math.round((head.importo_totale - q * padroncini.length) * 100) / 100;
    out[padroncini[0].id] = Math.round((q + resto) * 100) / 100;
    setQuote(out);
  };

  const salva = async () => {
    const rows = Object.entries(quote).filter(([, v]) => parseFloat(v) > 0)
      .map(([pid, v]) => ({ padroncino_id: pid, importo: parseFloat(v) }));
    if (!rows.length) return toast.error("Inserisci almeno una quota");
    try {
      const { data } = await api.post("/ripartizione", { ...head, company: azienda, quote: rows });
      toast.success(`${data.created} movimenti creati`); setQuote({});
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-heading font-black">Ripartizione massiva</h1>
        <div className="text-sm text-muted-foreground">Una fattura globale ripartita su più padroncini.</div>
      </div>
      <Card className="p-4 rounded-md grid grid-cols-2 md:grid-cols-6 gap-3">
        <F label="Azienda"><Select value={azienda} onValueChange={setAzienda}>
          <SelectTrigger className="h-8" data-testid="rip-company"><SelectValue /></SelectTrigger>
          <SelectContent>{["golmar", "vitam", "rotta66", "fmg"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select></F>
        <F label="Mese"><MonthPicker value={head.mese_competenza} className="h-8 w-full"
          onChange={(v) => setHead({ ...head, mese_competenza: v })} testId="rip-mese" /></F>
        <F label="Data fattura"><Input type="date" className="h-8" value={head.data}
          onChange={(e) => setHead({ ...head, data: e.target.value })} /></F>
        <F label="Categoria"><Select value={head.categoria} onValueChange={(v) => setHead({ ...head, categoria: v })}>
          <SelectTrigger className="h-8" data-testid="rip-categoria"><SelectValue /></SelectTrigger>
          <SelectContent>{Object.entries(CATEGORIE_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
        </Select></F>
        <F label="Descrizione"><Input className="h-8" value={head.descrizione}
          onChange={(e) => setHead({ ...head, descrizione: e.target.value })} data-testid="rip-descrizione" /></F>
        <F label="Totale fattura (€)"><Input type="number" step="0.01" className="h-8" value={head.importo_totale}
          onChange={(e) => setHead({ ...head, importo_totale: parseFloat(e.target.value || 0) })} data-testid="rip-totale" /></F>
      </Card>

      <Card className="rounded-md overflow-hidden">
        <div className="flex items-center justify-between p-3 border-b">
          <div className="text-sm">Somma quote: <span className="font-mono font-bold">{fmtEur(somma)}</span>
            <span className={`ml-3 font-mono ${Math.abs(diff) < 0.01 ? "text-green-600" : "text-destructive"}`} data-testid="rip-diff">
              Differenza: {fmtEur(diff)}</span></div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" className="h-8 gap-1" onClick={dividiEqui} data-testid="rip-equi"><Split className="w-4 h-4" />Dividi in parti uguali</Button>
            <Button size="sm" className="h-8" onClick={salva} disabled={!writable || Math.abs(diff) > 0.01} data-testid="rip-salva">Registra ripartizione</Button>
          </div>
        </div>
        <table className="autotrans-table w-full">
          <thead><tr><th>Codice</th><th>Padroncino</th><th className="w-48 text-right">Quota (€)</th></tr></thead>
          <tbody>
            {padroncini.length === 0 && <tr><td colSpan={3} className="text-center py-6 text-muted-foreground">Nessun padroncino attivo</td></tr>}
            {padroncini.map((p) => (
              <tr key={p.id}>
                <td className="font-mono">{p.codice}</td>
                <td>{p.ragione_sociale || `${p.nome} ${p.cognome || ""}`}</td>
                <td><Input type="number" step="0.01" className="h-7 text-right font-mono" value={quote[p.id] ?? ""}
                  onChange={(e) => setQuote({ ...quote, [p.id]: e.target.value })} data-testid={`rip-quota-${p.id}`} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

const F = ({ label, children }) => <div><Label className="text-xs font-heading uppercase tracking-wider">{label}</Label><div className="mt-1">{children}</div></div>;
